import { Inject, Injectable, OnDestroy } from "@angular/core";
import { delay, filter, Subscription, take, tap } from "rxjs";

import { VtoConfiguration } from "@hermes/api-model-vto";
import { WINDOW } from "@hermes/app-core";
import { ExternalLibraryFacade } from "@hermes/states/external-library";
import {
    NO_SHADE,
    NO_SELECT,
    VtoBeautyFacade,
    VtoSdkError,
} from "@hermes/states/vto-beauty";

import {
    calculateCameraDimensions,
    getSelectedSku,
} from "../helpers/ymk-sdk.helper";
import { InitArguments, YMK, YmkEvents } from "../types/ymk-sdk";

const PERFECT_CORP_SDK_KEY = "PERFECT_CORP_SDK";

@Injectable()
export class YmkSdkService implements OnDestroy {
    private sdkListeners: number[] = [];

    private subscription = new Subscription();

    constructor(
        public externalLibraryFacade: ExternalLibraryFacade,
        private vtoBeautyFacade: VtoBeautyFacade,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        @Inject(WINDOW) private window: Window & { YMK: YMK },
    ) {
        // autoload sdk
        this.subscription.add(
            this.vtoBeautyFacade.vtoConfiguration$
                .pipe(take(1))
                .subscribe((vtoConfiguration: VtoConfiguration) => {
                    this.externalLibraryFacade.load({
                        key: PERFECT_CORP_SDK_KEY,
                        windowAttribute: "YMK",
                        url: `${vtoConfiguration.ymkSdkConfiguration.sdkPath}/sdk.js?apiKey=${vtoConfiguration.ymkSdkConfiguration.apiKey}`,
                    });
                }),
        );
        this.vtoBeautyFacade.fetchVtoConfiguration();
    }

    public launchCamera(): void {
        this.subscription.add(
            this.externalLibraryFacade
                .isLoaded$(PERFECT_CORP_SDK_KEY)
                .pipe(
                    filter(Boolean),
                    tap(() => this.addSdkListeners()),
                    // To prevent a difference of dimension with the module
                    delay(50),
                    take(1),
                )
                .subscribe(() => {
                    const initConfig: InitArguments = {
                        ...calculateCameraDimensions(
                            this.window.innerWidth,
                            this.window.innerHeight,
                        ),
                        hideButtonsOnResultPage: true,
                        userConsent: "agree",
                        optOutGoogleAnalytics: true,
                    };
                    this.vtoBeautyFacade.updateCameraDimension({
                        width: initConfig.width,
                        height: initConfig.height,
                    });
                    this.window.YMK.init(initConfig);
                    // open PerfectCorp module instantly in live mode
                    this.window.YMK.open(true);
                }),
        );
    }

    public closeVto(): void {
        this.window.YMK?.close();
    }

    public applyMakeupBySku(sku: string): void {
        if (sku !== NO_SELECT) {
            this.window.YMK?.applyMakeupBySku(sku);
        }
    }

    public reset(): void {
        this.window.YMK?.reset();
    }

    public addSdkListeners(): void {
        this.removeSdkListeners();
        this.addSdkEventListener("loaded", () =>
            // setTimeout to prevent a delay to display camera in the module
            setTimeout(() => {
                this.vtoBeautyFacade.onAir();
            }, 500),
        );
        this.addSdkEventListener("cameraFailed", () =>
            this.vtoBeautyFacade.catchSdkError(VtoSdkError.ERROR_CAMERA),
        );
        this.addSdkEventListener("unsupportedResolution", () =>
            this.vtoBeautyFacade.catchSdkError(VtoSdkError.ERROR_RESOLUTION),
        );
    }

    public addSdkEventListener(event: YmkEvents, callback: unknown): void {
        this.sdkListeners.push(
            this.window.YMK?.addEventListener(event, callback),
        );
    }

    public applyMakeupConfiguration() {
        this.subscription.add(
            this.vtoBeautyFacade.selectAppliedConfiguration$.subscribe(
                (configuration) => {
                    if (configuration.currentVtoSku === NO_SHADE) {
                        const selectedSku = getSelectedSku(
                            configuration.skuApplied,
                        );
                        this.reset();
                        selectedSku.forEach((sku) => {
                            this.applyMakeupBySku(sku);
                        });
                        return;
                    }
                    this.applyMakeupBySku(configuration.currentVtoSku);
                },
            ),
        );
    }

    public ngOnDestroy(): void {
        this.removeSdkListeners();
        this.subscription.unsubscribe();
    }

    private removeSdkListeners(): void {
        for (const id of this.sdkListeners) {
            this.removeSdkEventListener(id);
        }
        this.sdkListeners = [];
    }

    private removeSdkEventListener(id: number): void {
        this.window.YMK?.removeEventListener(id);
    }
}
